import React from "react";
// Consumer Site Modal

// Default
import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Swal from "sweetalert2";
import moment from "moment";

// Mui
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import Stack from "@mui/material/Stack";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Tab from "@mui/material/Tab";
import TabContext from "@mui/lab/TabContext";
import TabList from "@mui/lab/TabList";
import TabPanel from "@mui/lab/TabPanel";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Grid from "@mui/material/Grid2";
import Divider from "@mui/material/Divider";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";

// Custom
import CustomInput from "../../forms/input/Input";
import CustomOutlinedSelect from "../../forms/select/OutlinedCustomSelect";
import ContainedButton from "../../forms/button/ContainedButton";
import CustomSearchBox from "../search/SearchBox";
import CustomSearch from "../search/Search";
import CustomChip from "../chip/Chip";
import CustomSwitch from "../switch/Switch";
import DatePickerInput from "../../forms/input/DatePickerInput";

// Utils
import { fetchGETRequest, fetchPOSTRequest } from "../../utils/services";
import { useCustomFields } from "../../context/CustomFieldsProvider";

// React icons
import { FaRegTrashAlt } from "react-icons/fa";
import { ToastState } from "../../types";
import { UnifiedIcon } from "../Icon/UnifiedIcon";

const ConsumerSiteModal = ({
  openModal,
  setOpenModal,
  setToast,
  addOrEdit,
  nodes,
  setNodes,
  handleCustomerAndAssetSearch,
}: {
  openModal: { id: string; type: string };
  setOpenModal: (value: { id: string; type: string }) => void;
  setToast: (value: ToastState) => void;
  addOrEdit: "add" | "edit" | null;
  nodes: { id: string; type: string }[];
  setNodes: (value: { id: string; type: string }[]) => void;
  handleCustomerAndAssetSearch: () => void;
}) => {
  const [searchValue, setSearchValue] = useState({
    type: "id",
    value: "",
  });
  const [searchOpen, setSearchOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("Details");
  const [siteData, setSiteData] = useState<{
    id: { id: string; entityType: string };
    useAsLoadProfile: boolean;
    serviceCommodityType: { value: string; label: string };
    serviceType: { value: string; label: string };
    operatingHours: string;
    latitude: string;
    longitude: string;
    locationId: string;
    substationName: string;
    zoneNumber: string;
    feederName: string;
    dtrName: string;
    feederPillarNo: string;
    meterLocation: string;
    changeOfMeterLocation: string;
    dateOfConnection: Date | null;
    [key: string]: any;
  }>({
    id: { id: "", entityType: "" },
    useAsLoadProfile: false,
    serviceCommodityType: { value: "", label: "" },
    serviceType: { value: "", label: "" },
    operatingHours: "",
    latitude: "",
    longitude: "",
    locationId: "",
    substationName: "",
    zoneNumber: "",
    feederName: "",
    dtrName: "",
    feederPillarNo: "",
    meterLocation: "",
    changeOfMeterLocation: "",
    dateOfConnection: null,
  });
  const [activeMeters, setActiveMeters] = useState<string[]>([]);
  const [inactiveMeters, setInactiveMeters] = useState<string[]>([]);
  const [names, setNames] = useState<string[]>([]);
  const [counter, setCounter] = useState(1);
  const [linkToCustomer, setLinkToCustomer] = useState<string>("");

  const navigate = useNavigate();

  const { toggleCustomFieldDrawer, createdFields } = useCustomFields();

  const location = useLocation();

  const TABS = ["Details", "Coordinates", "Meters"];

  const commodityTypeOptions = [
    { label: "Electricity", value: "Electricity" },
    { label: "Water", value: "Water" },
    { label: "Gas", value: "Gas" },
  ];

  const serviceTypeOptions = [
    { label: "Commercial", value: "Commercial" },
    { label: "Residential", value: "Residential" },
  ];

  const startDate = moment(siteData?.dateOfConnection);
  const today = moment();
  const isActive = startDate?.isBefore(today);

  const generateUniqueName = () => {
    const newName = `Site${new Date().getMilliseconds()}`;
    setNames([...names, newName]);
    setCounter(counter + 1);
    return newName;
  };

  const addRow = () => {
    setActiveMeters((prevState) => [...prevState, ""]);
  };

  const removeRow = (index: number) => {
    const updatedMeters = activeMeters.filter((_, idx) => index !== idx);
    setActiveMeters([...updatedMeters]);
  };

  const handleMeterIdChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    index: number,
    isActive: boolean,
  ) => {
    const { value } = e.target;
    if (isActive) {
      const updatedActiveMeters = [...activeMeters];
      updatedActiveMeters[index] = value;
      setActiveMeters(updatedActiveMeters);
    } else {
      const updatedInactiveMeters = [...inactiveMeters];
      updatedInactiveMeters[index] = value;
      setInactiveMeters(updatedInactiveMeters);
    }
  };

  const handleGoToMeter = (meterid: string) => {
    navigate("/meters", { state: { meterid: meterid } });
  };

  const handleSubmit = async () => {
    try {
      const requestData = {
        id: { id: "", entityType: "" },
        tenantId: {
          id: "e936e050-e1d7-11ee-8baa-37d11d926d57",
          entityType: "TENANT",
        },
        name: generateUniqueName(),
        type: "SITE",
        label: "NY Building",
        additionalInfo: {
          UseAsLoadProfile: siteData.useAsLoadProfile,
          serviceCommodityType: siteData.serviceCommodityType?.value,
          serviceType: siteData.serviceType?.value,
          operatingHours: siteData.operatingHours,
          latitude: siteData.latitude,
          longitude: siteData.longitude,
          activeMeters: activeMeters,
          locationId: siteData.locationId,
          substationName: siteData.substationName,
          zoneNumber: siteData.zoneNumber,
          feederName: siteData.feederName,
          dtrName: siteData.dtrName,
          feederPillarNo: siteData.feederPillarNo,
          meterLocation: siteData.meterLocation,
          changeOfMeterLocation: siteData.changeOfMeterLocation,
          dateOfConnection: siteData.dateOfConnection,
        },
      };

      if (siteData?.id && siteData?.id?.id && addOrEdit === "edit") {
        requestData.id = {
          id: siteData?.id?.id,
          entityType: "ASSET",
        };
      }

      if (siteData?.locationId) {
        let secondResponse = null;
        const response = await fetchPOSTRequest(`asset`, requestData);

        if (response && linkToCustomer) {
          secondResponse = await fetchPOSTRequest(
            `customer/${linkToCustomer}/asset/${response?.id?.id}`,
            requestData,
            true,
          );
        }

        setSearchValue({ ...searchValue, value: response?.id?.id });
        setSiteData({
          id: { id: "", entityType: "" },
          useAsLoadProfile: false,
          serviceCommodityType: { value: "", label: "" },
          serviceType: { value: "", label: "" },
          operatingHours: "",
          latitude: "",
          longitude: "",
          locationId: "",
          substationName: "",
          zoneNumber: "",
          feederName: "",
          dtrName: "",
          feederPillarNo: "",
          meterLocation: "",
          changeOfMeterLocation: "",
          dateOfConnection: null,
        });

        if (addOrEdit === "add") {
          const updatedNodes = nodes?.map(
            (node: { id: string; type: string }) => {
              if (node?.id?.toUpperCase() === "SITE") {
                const newObj = {
                  created: true,
                  label: "Successfully created",
                  link: "",
                  title: "Site",
                  value: response?.id?.id,
                };
                return {
                  ...node,
                  data: newObj,
                };
              }
              return node;
            },
          );

          setNodes([...updatedNodes]);
        } else {
          handleCustomerAndAssetSearch();
        }

        setActiveMeters([]);
        setLinkToCustomer("");

        setOpenModal({
          type: "",
          id: "",
        });

        if (secondResponse?.status === 200) {
          Swal.fire({
            icon: "success",
            title: "Success",
            text: response?.id?.id + " " + "Update successful!",
          });
        } else {
          setToast({
            message: response?.message,
            severity: "error",
          });
        }
      } else {
        setToast({
          message: "Location Id Field Required",
          severity: "error",
        });
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Error during request",
      });
    }
  };

  const handleSiteSearch = async (payload?: string) => {
    try {
      let searchTerm = "";

      if (searchValue?.value.trim()) {
        searchTerm = searchValue?.value;
      } else if (payload) {
        searchTerm = payload;
      } else {
        setToast({
          message: "Please enter a valid search value.",
          severity: "error",
        });
        return;
      }

      const response = await fetchGETRequest(`asset/${searchTerm}`, {}, true);

      if (response?.data?.additionalInfo) {
        const {
          useAsLoadProfile,
          serviceCommodityType,
          serviceType,
          operatingHours,
          latitude,
          longitude,
          activeMeters,
          locationId,
          substationName,
          zoneNumber,
          feederName,
          dtrName,
          feederPillarNo,
          meterLocation,
          changeOfMeterLocation,
          dateOfConnection,
        } = response?.data?.additionalInfo;
        setSiteData({
          id: response?.data?.id,
          useAsLoadProfile: useAsLoadProfile,
          operatingHours: operatingHours,
          latitude: latitude,
          longitude: longitude,
          locationId: locationId,
          substationName: substationName,
          zoneNumber: zoneNumber,
          feederName: feederName,
          dtrName: dtrName,
          feederPillarNo: feederPillarNo,
          meterLocation: meterLocation,
          changeOfMeterLocation: changeOfMeterLocation,
          dateOfConnection: dateOfConnection
            ? new Date(dateOfConnection)
            : null,
          serviceCommodityType: commodityTypeOptions.find(
            (item) => item.value === serviceCommodityType,
          ) || { value: "", label: "" },
          serviceType: serviceTypeOptions.find(
            (item) => item.value === serviceType,
          ) || { value: "", label: "" },
        });
        setActiveMeters(activeMeters);
        setLinkToCustomer(response?.data?.customerId?.id);
        // setSearchValue({
        //   type: "id",
        //   value: "",
        // });
      } else {
        setToast({ message: "Site not found", severity: "error" });
      }
      setSearchOpen(false);
    } catch (error: any) {
      console.error("Error fetching consumer:", error.message);
    }
  };

  const getCustomFields = (item: any) => {
    switch (item?.type?.label) {
      case "Date":
        return (
          <DatePickerInput
            label={item?.title}
            format={"dd/MM/yyyy"}
            placeholder={"dd/mm/yyyy"}
            value={siteData?.[item?.title?.toUpperCase()] || ""}
            onChange={(event) => {
              setSiteData({
                ...siteData,
                [item?.title?.toUpperCase()]: event,
              });
            }}
          />
        );
      case "Checkbox":
        return (
          <Stack mt={"30px"}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={siteData?.[item?.title?.toUpperCase()] || false}
                  onChange={(event) => {
                    setSiteData({
                      ...siteData,
                      [item?.title?.toUpperCase()]: event.target.checked,
                    });
                  }}
                  sx={{
                    color: "#1976d2",
                    "&.Mui-checked": {
                      color: "#1976d2",
                    },
                  }}
                />
              }
              label={item?.title}
            />
          </Stack>
        );
      case "Text":
        return (
          <>
            <CustomInput
              label={item?.title}
              placeholder={item?.title}
              type="text"
              value={siteData?.[item?.title?.toUpperCase()] || ""}
              onChange={(event) => {
                setSiteData({
                  ...siteData,
                  [item?.title?.toUpperCase()]: event.target.value,
                });
              }}
            />
          </>
        );

      case "Number":
        return (
          <>
            <CustomInput
              label={item?.title}
              placeholder={item?.title}
              type="number"
              value={siteData?.[item?.title?.toUpperCase()] || ""}
              onChange={(event) => {
                setSiteData({
                  ...siteData,
                  [item?.title?.toUpperCase()]: event.target.value,
                });
              }}
            />
          </>
        );

      case "Dropdown":
        return (
          <>
            <CustomOutlinedSelect
              label={item?.title}
              placeholder={item?.title}
              options={item?.options}
              value={siteData?.[item?.title?.toUpperCase()] || ""}
              onChange={(event) => {
                setSiteData({
                  ...siteData,
                  [item?.title?.toUpperCase()]: event,
                });
              }}
            />
          </>
        );

      default:
        return null;
    }
  };

  useEffect(() => {
    if (openModal?.id && openModal?.type === "SITE") {
      setSearchValue({ ...searchValue, value: openModal?.id });
      handleSiteSearch(openModal?.id);
    }
  }, [openModal]);

  useEffect(() => {
    if (location?.state?.siteId) {
      setSearchValue({ ...searchValue, value: location?.state?.siteId });
      handleSiteSearch(location?.state?.siteId);
    }
  }, [location]);

  return (
    <Dialog
      onClose={() => setOpenModal({ type: "", id: "" })}
      open={openModal?.type === "SITE"}
      fullWidth
      maxWidth={"md"}
    >
      <Stack
        direction={"row"}
        justifyContent={"space-between"}
        alignItems={"center"}
      >
        <DialogTitle sx={{ m: 0, p: 2 }}>Site Information</DialogTitle>
        <IconButton
          onClick={() => setOpenModal({ type: "", id: "" })}
          sx={{ marginRight: "10px" }}
        >
          <CloseIcon />
        </IconButton>
      </Stack>

      <DialogContent dividers>
        <Card
          sx={{
            overflow: "visible",
            position: "relative",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="body1" fontWeight={600}>
            Site ID
          </Typography>

          <Stack direction={"row"} gap={"10px"} alignItems={"center"}>
            <CustomSearchBox
              placeholder={"Enter 10 Digit"}
              width={"500px"}
              height={"40px"}
              handleSearchToggle={() => setSearchOpen(!searchOpen)}
              searchOpen={searchOpen}
              dialogBody={
                <Stack direction={"column"} gap={"20px"}>
                  <CustomInput
                    label={"Type"}
                    value={searchValue?.type}
                    onChange={(e) =>
                      setSearchValue({
                        ...searchValue,
                        type: e.target.value,
                      })
                    }
                  />
                  <CustomInput
                    label={"Value"}
                    value={searchValue?.value}
                    onChange={(e) =>
                      setSearchValue({
                        ...searchValue,
                        value: e.target.value,
                      })
                    }
                  />
                  <ContainedButton
                    label={"Submit"}
                    sx={{
                      width: "100%",
                      fontWeight: 600,
                      color: "#ffffff",
                    }}
                    onClick={handleSiteSearch}
                  />
                </Stack>
              }
            />
          </Stack>
        </Card>

        <Card sx={{ mt: "20px" }}>
          <TabContext value={activeTab}>
            <Box
              sx={{
                borderBottom: 1,
                borderColor: "#CACDD7",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <TabList
                onChange={(_, newValue) => setActiveTab(newValue)}
                scrollButtons={false}
                TabIndicatorProps={{
                  style: {
                    backgroundColor: "##1280E7",
                    height: "3px",
                  },
                }}
              >
                {TABS.map((item, index) => {
                  return (
                    <Tab
                      key={index}
                      label={
                        <Box
                          display="flex"
                          alignItems="center"
                          gap={"7px"}
                          fontWeight={500}
                        >
                          {item}
                        </Box>
                      }
                      value={item}
                      className="consumer_right_card_list_tab"
                    />
                  );
                })}
              </TabList>

              <ContainedButton
                label={"Create custom fields"}
                sx={{
                  width: "fit-content",
                  fontWeight: 600,
                  color: "#ffffff",
                }}
                onClick={() => toggleCustomFieldDrawer("Site", true)}
              />
            </Box>

            <CardContent>
              <TabPanel value={TABS[0]} sx={{ padding: 0 }}>
                <Stack
                  direction={"row"}
                  justifyContent={"space-between"}
                  alignItems={"center"}
                >
                  <Stack direction={"row"} gap={"5px"}>
                    <Typography variant="body1">Status:</Typography>
                    <CustomChip
                      label={isActive ? "Active " : "Inactive"}
                      sx={{
                        minWidth: "auto",
                      }}
                      backgroundColor={isActive ? "#9CE976" : "#FF3B30"}
                      color="#ffffff"
                    />
                  </Stack>

                  <CustomSwitch
                    label={"Use As Load Profile"}
                    value={siteData?.useAsLoadProfile}
                    onChange={() => {
                      setSiteData({
                        ...siteData,
                        useAsLoadProfile: !siteData.useAsLoadProfile,
                      });
                    }}
                  />
                </Stack>

                <Grid container spacing={2} mt={"10px"}>
                  <Grid size={{ xs: 12, md: 4, lg: 4 }}>
                    <CustomOutlinedSelect
                      label={"Service Commodity Type"}
                      options={commodityTypeOptions}
                      placeholder={"Select"}
                      name={"serviceCommodityType"}
                      value={siteData?.serviceCommodityType}
                      onChange={(newValue) =>
                        setSiteData({
                          ...siteData,
                          serviceCommodityType: newValue as {
                            value: string;
                            label: string;
                          },
                        })
                      }
                    />
                  </Grid>

                  <Grid size={{ xs: 12, md: 4, lg: 4 }}>
                    <CustomOutlinedSelect
                      label={"Service Type"}
                      options={serviceTypeOptions}
                      placeholder={"Select"}
                      name={"serviceType"}
                      value={siteData?.serviceType}
                      onChange={(newValue) =>
                        setSiteData({
                          ...siteData,
                          serviceType: newValue as {
                            value: string;
                            label: string;
                          },
                        })
                      }
                    />
                  </Grid>

                  <Grid size={{ xs: 12, md: 4, lg: 4 }}>
                    <CustomInput
                      label={"Operating Hours"}
                      placeholder={"Operating Hours"}
                      name={"operatingHours"}
                      value={siteData?.operatingHours}
                      onChange={(e) =>
                        setSiteData({
                          ...siteData,
                          operatingHours: e.target.value,
                        })
                      }
                    />
                  </Grid>

                  <Grid size={{ xs: 12, md: 4, lg: 4 }}>
                    <CustomInput
                      label={"Link to Consumer"}
                      placeholder={"Link to Consumer"}
                      name={"linkToCustomer"}
                      onChange={(e) => setLinkToCustomer(e.target.value)}
                      value={linkToCustomer}
                    />
                  </Grid>

                  <Grid size={{ xs: 12, md: 4, lg: 4 }}>
                    <CustomInput
                      label={"Location Id"}
                      placeholder={"Location Id"}
                      name={"locationId"}
                      required={true}
                      onChange={(e) =>
                        setSiteData({
                          ...siteData,
                          locationId: e.target.value,
                        })
                      }
                      value={siteData?.locationId}
                    />
                  </Grid>

                  <Grid size={{ xs: 12, md: 4, lg: 4 }}>
                    <CustomInput
                      label={"Substation Name"}
                      placeholder={"Substation Name"}
                      name={"substationName"}
                      value={siteData?.substationName}
                      onChange={(e) =>
                        setSiteData({
                          ...siteData,
                          substationName: e.target.value,
                        })
                      }
                    />
                  </Grid>

                  <Grid size={{ xs: 12, md: 4, lg: 4 }}>
                    <CustomInput
                      label={"Zone Number"}
                      placeholder={"Zone Number"}
                      name={"zoneNumber"}
                      value={siteData?.zoneNumber}
                      onChange={(e) =>
                        setSiteData({
                          ...siteData,
                          zoneNumber: e.target.value,
                        })
                      }
                    />
                  </Grid>

                  <Grid size={{ xs: 12, md: 4, lg: 4 }}>
                    <CustomInput
                      label={"Feeder Name"}
                      placeholder={"Feeder Name"}
                      name={"feederName"}
                      value={siteData?.feederName}
                      onChange={(e) =>
                        setSiteData({
                          ...siteData,
                          feederName: e.target.value,
                        })
                      }
                    />
                  </Grid>

                  <Grid size={{ xs: 12, md: 4, lg: 4 }}>
                    <CustomInput
                      label={"DTR Name"}
                      placeholder={"DTR Name"}
                      name={"dtrName"}
                      value={siteData?.dtrName}
                      onChange={(e) =>
                        setSiteData({
                          ...siteData,
                          dtrName: e.target.value,
                        })
                      }
                    />
                  </Grid>

                  <Grid size={{ xs: 12, md: 4, lg: 4 }}>
                    <CustomInput
                      label={"Feeder Pillar No."}
                      placeholder={"Feeder Pillar No."}
                      name={"feederPillarNo"}
                      value={siteData?.feederPillarNo}
                      onChange={(e) =>
                        setSiteData({
                          ...siteData,
                          feederPillarNo: e.target.value,
                        })
                      }
                    />
                  </Grid>

                  <Grid size={{ xs: 12, md: 4, lg: 4 }}>
                    <CustomInput
                      label={"Meter Location"}
                      placeholder={"Meter Location"}
                      name={"meterLocation"}
                      value={siteData?.meterLocation}
                      onChange={(e) =>
                        setSiteData({
                          ...siteData,
                          meterLocation: e.target.value,
                        })
                      }
                    />
                  </Grid>

                  <Grid size={{ xs: 12, md: 4, lg: 4 }}>
                    <CustomInput
                      label={"Change of Meter Location Envisaged"}
                      placeholder={"Change of Meter Location Envisaged"}
                      name={"changeOfMeterLocation"}
                      value={siteData?.changeOfMeterLocation}
                      onChange={(e) =>
                        setSiteData({
                          ...siteData,
                          changeOfMeterLocation: e.target.value,
                        })
                      }
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 4, lg: 4 }}>
                    <DatePickerInput
                      label={"Date of Connection"}
                      placeholder={"dd/mm/yyyy hh:mm"}
                      name={"dateOfConnection"}
                      onChange={(event) => {
                        setSiteData({
                          ...siteData,
                          dateOfConnection: event,
                        });
                      }}
                      value={siteData?.dateOfConnection}
                    />
                  </Grid>
                </Grid>

                {createdFields?.["Site"] && (
                  <>
                    <Stack sx={{ paddingTop: "30px", paddingBottom: "20px" }}>
                      <Divider sx={{ borderWidth: "1.5px" }} />
                    </Stack>

                    <Grid container spacing={2}>
                      {createdFields?.["Site"]?.map(
                        (item: any, index: number) => {
                          return (
                            <Grid
                              size={{ xs: 12, md: 4, lg: 4 }}
                              mt={"10px"}
                              key={index}
                            >
                              {getCustomFields(item)}
                            </Grid>
                          );
                        },
                      )}
                    </Grid>
                  </>
                )}
              </TabPanel>

              <TabPanel value={TABS[1]} sx={{ padding: 0 }}>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, md: 4, lg: 4 }}>
                    <CustomInput
                      label={"Latitude"}
                      placeholder={"Latitude"}
                      name={"latitude"}
                      value={siteData?.latitude}
                      onChange={(e) =>
                        setSiteData({
                          ...siteData,
                          latitude: e.target.value,
                        })
                      }
                    />
                  </Grid>

                  <Grid size={{ xs: 12, md: 4, lg: 4 }}>
                    <CustomInput
                      label={"Longitude"}
                      placeholder={"Longitude"}
                      name={"longitude"}
                      onChange={(e) =>
                        setSiteData({
                          ...siteData,
                          longitude: e.target.value,
                        })
                      }
                      value={siteData?.longitude}
                    />
                  </Grid>
                </Grid>
              </TabPanel>

              <TabPanel value={TABS[2]} sx={{ padding: 0 }}>
                <Box display={"flex"} gap={"20px"}>
                  <Stack width={"60%"}>
                    {activeMeters?.map((item, index) => (
                      <>
                        <Stack
                          key={index}
                          direction={"row"}
                          gap={"20px"}
                          alignItems={"center"}
                          sx={{ marginBottom: "16px" }}
                        >
                          <Typography variant="body1" fontWeight={600}>
                            Meter ID:
                          </Typography>

                          <CustomSearch
                            placeholder={"10 digits"}
                            sx={{
                              width: "400px",
                              height: "40px",
                            }}
                            value={item}
                            onChange={(e) =>
                              handleMeterIdChange(e, index, true)
                            }
                            onClick={() => handleGoToMeter(item)}
                          />

                          <Stack direction={"row"} gap={"10px"}>
                            <ContainedButton
                              label={"Go to Meter"}
                              sx={{
                                width: "fit-content",
                                fontWeight: 600,
                                height: "35px",
                                color: "#ffffff",
                              }}
                              onClick={() => handleGoToMeter(item)}
                            />

                            <Box
                              sx={{
                                backgroundColor: "#FA6B6B",
                                width: "35px",
                                height: "35px",
                                borderRadius: "4px",
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                              }}
                              onClick={() => removeRow(index)}
                            >
                              <UnifiedIcon
                                icon={FaRegTrashAlt}
                                color="#ffffff"
                                style={{ marginBottom: "2px" }}
                              />
                            </Box>
                          </Stack>
                        </Stack>
                      </>
                    ))}
                  </Stack>
                </Box>

                <ContainedButton
                  label={"Add Row"}
                  sx={{
                    width: "fit-content",
                    fontWeight: 600,
                    color: "#ffffff",
                  }}
                  onClick={addRow}
                />
              </TabPanel>

              <Stack
                direction={"row"}
                justifyContent={"space-between"}
                alignItems={"center"}
                mt={"20px"}
              >
                {/* <ContainedButton
                label={"Install Meter"}
                
                sx={{
                  width: "fit-content",
                  fontWeight: 600,
                  height: "35px",
                  color: "#ffffff",
                }}
                onClick={() => alert("coming soon...")}
              /> */}
              </Stack>
            </CardContent>
          </TabContext>
        </Card>

        <DialogActions>
          <ContainedButton
            label={"Save"}
            sx={{
              width: "fit-content",
              fontWeight: 600,
              color: "#ffffff",
            }}
            onClick={handleSubmit}
            disabled={!siteData?.locationId}
          />
          <ContainedButton
            label={"Close"}
            bgColor={"linear-gradient(180deg, #FF7F7F 0%, #FF4D4D 100%)"}
            sx={{
              fontWeight: 600,
              color: "#ffffff",
            }}
            onClick={() => setOpenModal({ type: "", id: "" })}
          />
        </DialogActions>
      </DialogContent>
    </Dialog>
  );
};

export default ConsumerSiteModal;
